import React, { useState, useEffect } from 'react';
import { Plus, Eye } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { storiesService, StoryGroup } from '../services/storiesService';
import StoryViewer from './StoryViewer';
import CreateStoryModal from './CreateStoryModal';
import { useLanguage } from '../context/LanguageContext';

const StoriesSection: React.FC = () => {
  const { user } = useAuth();
  const { select } = useLanguage();
  const [storyGroups, setStoryGroups] = useState<StoryGroup[]>([]);
  const [allStoryGroups, setAllStoryGroups] = useState<StoryGroup[]>([]);
  const [myStories, setMyStories] = useState<Record<string, unknown>[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedGroup, setSelectedGroup] = useState<StoryGroup | null>(null);
  const [selectedGroupIndex, setSelectedGroupIndex] = useState(0);
  const [showViewer, setShowViewer] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);

  useEffect(() => {
    loadStories();
    loadMyStories();
  }, []);

  const loadStories = async () => {
    try {
      const data = await storiesService.getActiveStories();
      setStoryGroups(data);
      // Create combined list including user's stories for navigation
      const combined = [];
      if (myStories.length > 0 && user) {
        combined.push({
          user: user,
          stories: myStories,
          latest_story: myStories[0]?.created_at || '',
          is_viewed: true,
        });
      }
      combined.push(...data);
      setAllStoryGroups(combined);
    } catch (err) {
      console.error('Error loading stories:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadMyStories = async () => {
    if (!user) return;

    try {
      const data = await storiesService.getMyStories();
      setMyStories(data);
    } catch (err) {
      console.error('Error loading my stories:', err);
    }
  };

  const handleStoryGroupClick = (group: StoryGroup) => {
    const index = allStoryGroups.findIndex(g => g.user.id === group.user.id);
    setSelectedGroup(group);
    setSelectedGroupIndex(index);
    setShowViewer(true);
  };

  const handleCloseViewer = () => {
    setShowViewer(false);
    setSelectedGroup(null);
    // Refresh stories to update view status
    loadStories();
    loadMyStories();
  };

  const handleCreateStory = async () => {
    await loadStories();
    await loadMyStories();
  };

  const handleNextGroup = () => {
    const nextIndex = selectedGroupIndex + 1;
    if (nextIndex < allStoryGroups.length) {
      setSelectedGroup(allStoryGroups[nextIndex]);
      setSelectedGroupIndex(nextIndex);
    } else {
      handleCloseViewer();
    }
  };

  const handlePreviousGroup = () => {
    const prevIndex = selectedGroupIndex - 1;
    if (prevIndex >= 0) {
      setSelectedGroup(allStoryGroups[prevIndex]);
      setSelectedGroupIndex(prevIndex);
    }
  };

  if (loading) {
    return (
      <div className="flex space-x-4 p-4 overflow-x-auto">
        {[1, 2, 3, 4, 5].map(i => (
          <div key={i} className="flex-shrink-0">
            <div className="w-16 h-16 bg-gray-200 rounded-full animate-pulse mb-2"></div>
            <div className="w-16 h-3 bg-gray-200 rounded animate-pulse"></div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <>
      <div className="bg-white rounded-xl border border-gray-200 p-4 mb-6">
        <div className="flex items-center space-x-4 overflow-x-auto pb-2">
          {/* Add Story Button */}
          <div className="flex-shrink-0 text-center">
            <button
              onClick={() => setShowCreateModal(true)}
              className="relative w-16 h-16 rounded-full border-2 border-dashed border-purple-300 flex items-center justify-center hover:border-purple-500 transition-colors group"
            >
              <Plus className="text-purple-500 group-hover:text-purple-700" size={24} />
            </button>
            <p className="text-xs text-gray-600 mt-2 truncate w-16">{select({ es: 'Tu story', en: 'Your story' })}</p>
          </div>

          {/* My Stories */}
          {myStories.length > 0 && (
            <div className="flex-shrink-0 text-center">
              <button
                onClick={() => user && handleStoryGroupClick({
                  user: user,
                  stories: myStories,
                  latest_story: myStories[0]?.created_at || '',
                  is_viewed: true, // Own stories are always "viewed"
                })}
                className="relative group"
              >
                <div className="w-16 h-16 rounded-full p-1 bg-gradient-to-tr from-green-400 via-blue-500 to-purple-600">
                  <img
                    src={user?.avatar_url || 'https://images.pexels.com/photos/1181519/pexels-photo-1181519.jpeg?auto=compress&cs=tinysrgb&w=64&h=64&fit=crop'}
                    alt={user?.full_name}
                    className="w-full h-full rounded-full border-2 border-white object-cover"
                  />
                </div>
                <div className="absolute bottom-0 right-0 bg-green-600 text-white rounded-full p-1">
                  <Eye size={10} />
                </div>
              </button>
              <p className="text-xs text-gray-600 mt-2 truncate w-16">{select({ es: 'Mis stories', en: 'My stories' })}</p>
            </div>
          )}

          {/* Other Users' Stories */}
          {storyGroups.map((group) => (
            <div key={group.user.id} className="flex-shrink-0 text-center">
              <button
                onClick={() => handleStoryGroupClick(group)}
                className="relative group"
              >
                <div className={`w-16 h-16 rounded-full p-1 ${group.is_viewed
                    ? 'bg-gray-300'
                    : 'bg-gradient-to-tr from-purple-400 via-pink-500 to-red-500'
                  }`}>
                  <img
                    src={group.user.avatar_url || 'https://images.pexels.com/photos/1181519/pexels-photo-1181519.jpeg?auto=compress&cs=tinysrgb&w=64&h=64&fit=crop'}
                    alt={group.user.full_name}
                    className="w-full h-full rounded-full border-2 border-white object-cover"
                  />
                </div>
                {group.user.is_premium && (
                  <div className="absolute top-0 right-0 bg-yellow-500 text-white rounded-full p-1">
                    <span className="text-xs">✨</span>
                  </div>
                )}
              </button>
              <p className="text-xs text-gray-600 mt-2 truncate w-16">
                {group.user.username}
              </p>
            </div>
          ))}

          {storyGroups.length === 0 && myStories.length === 0 && (
            <div className="flex-1 text-center py-8">
              <p className="text-gray-500">{select({ es: 'No hay stories disponibles', en: 'No stories available' })}</p>
              <p className="text-sm text-gray-400">{select({ es: '¡Sé el primero en compartir un momento!', en: 'Be the first to share a moment!' })}</p>
            </div>
          )}
        </div>
      </div>

      {/* Story Viewer */}
      {showViewer && selectedGroup && (
        <StoryViewer
          storyGroup={selectedGroup}
          onClose={handleCloseViewer}
          onNext={handleNextGroup}
          onPrevious={handlePreviousGroup}
        />
      )}

      {/* Create Story Modal */}
      {showCreateModal && (
        <CreateStoryModal
          onClose={() => setShowCreateModal(false)}
          onStoryCreated={handleCreateStory}
        />
      )}
    </>
  );
};

export default StoriesSection;
